import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";
import { DESTINATIONS } from "@/lib/qr";

const CATEGORIES = ["General", "Business", "Marketing", "Media", "Events", "Feedback"];

export default function AdminQRTypes() {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/qr-types");
      const cfg = data.types || {};
      setPlans(data.plans || []);
      api.get("/admin/qr-types/stats").then(({ data: s }) => setStats(s.stats || {})).catch(() => {});
      const merged = DESTINATIONS.filter((d) => !d.hidden).map((d, i) => {
        const c = cfg[d.id] || {};
        return {
          id: d.id,
          base: d,
          enabled: c.enabled !== false,
          display_name: c.display_name || d.name,
          icon: c.icon || d.icon,
          description: c.description || d.desc,
          category: c.category || d.cat,
          featured: c.featured ?? !!d.featured,
          plans: c.plans || [],
          order: c.order ?? (Object.keys(cfg).length ? 999 + i : i),
        };
      });
      merged.sort((a, b) => a.order - b.order);
      setRows(merged.map((r, i) => ({ ...r, order: i })));
    } catch (e) {
      toast.error("Could not load QR types (need qr_controls permission)");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const patch = (id, upd) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...upd } : r)));

  const togglePlan = (id, key) => setRows((rs) => rs.map((r) => {
    if (r.id !== id) return r;
    const has = r.plans.includes(key);
    return { ...r, plans: has ? r.plans.filter((p) => p !== key) : [...r.plans, key] };
  }));

  const move = (from, to) => {
    if (to < 0 || to >= rows.length) return;
    setRows((rs) => {
      const next = [...rs];
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next.map((r, i) => ({ ...r, order: i }));
    });
  };

  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    move(dragIdx, idx);
    setDragIdx(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        types: rows.map((r, i) => ({
          id: r.id, enabled: r.enabled, display_name: r.display_name, icon: r.icon,
          description: r.description, category: r.category, featured: r.featured,
          order: i, plans: r.plans,
        })),
      };
      await api.put("/admin/qr-types", payload);
      toast.success("QR type settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = useMemo(() => rows.filter((r) => r.enabled).length, [rows]);

  return (
    <AdminLayout
      title="QR Types"
      subtitle="Control which QR destination types customers can create, their labels, order and plan access."
      actions={
        <button data-testid="qrtypes-save" onClick={save} disabled={saving || loading}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
          {saving ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Save" className="h-4 w-4" />}
          Save changes
        </button>
      }>

      <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2"><Icon name="QrCode" className="h-4 w-4 text-primary" /><span className="font-semibold">{rows.length}</span> types</div>
        <div className="flex items-center gap-2"><Icon name="CheckCircle2" className="h-4 w-4 text-emerald-500" /><span className="font-semibold">{enabledCount}</span> enabled</div>
        <div className="text-muted-foreground">Drag to reorder. Disabling a type only hides it from new creation — existing QR codes keep working.</div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="mt-4 space-y-2" data-testid="qrtypes-list">
          {rows.map((r, idx) => (
            <div key={r.id} data-testid={`qrtype-row-${r.id}`}
              draggable onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
              className={`rounded-2xl border bg-card p-4 transition-colors ${r.enabled ? "border-border" : "border-border/60 opacity-70"} ${dragIdx === idx ? "ring-2 ring-primary" : ""}`}>
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center gap-1 pt-1">
                  <Icon name="GripVertical" className="h-4 w-4 text-muted-foreground cursor-grab" />
                  <div className="flex flex-col">
                    <button onClick={() => move(idx, idx - 1)} className="text-muted-foreground hover:text-foreground" title="Move up"><Icon name="ChevronUp" className="h-3.5 w-3.5" /></button>
                    <button onClick={() => move(idx, idx + 1)} className="text-muted-foreground hover:text-foreground" title="Move down"><Icon name="ChevronDown" className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center shrink-0"><Icon name={r.icon} className="h-5 w-5 text-primary" /></div>

                <div className="flex-1 min-w-0 grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Display name</label>
                    <input data-testid={`qrtype-name-${r.id}`} value={r.display_name} onChange={(e) => patch(r.id, { display_name: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      <span>id: <code>{r.id}</code> · modes: {r.base.modes.join(", ")}</span>
                      <span data-testid={`qrtype-stat-count-${r.id}`} className="inline-flex items-center gap-1 text-foreground/70 font-medium"><Icon name="QrCode" className="h-3 w-3" />{(stats[r.id]?.count) || 0} codes</span>
                      <span data-testid={`qrtype-stat-scans-${r.id}`} className="inline-flex items-center gap-1 text-emerald-600 font-medium"><Icon name="ScanLine" className="h-3 w-3" />{(stats[r.id]?.scans) || 0} scans</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Icon</label>
                      <input data-testid={`qrtype-icon-${r.id}`} value={r.icon} onChange={(e) => patch(r.id, { icon: e.target.value })}
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Category</label>
                      <select data-testid={`qrtype-cat-${r.id}`} value={r.category} onChange={(e) => patch(r.id, { category: e.target.value })}
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm">
                        {[...new Set([r.category, ...CATEGORIES])].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Description</label>
                    <input data-testid={`qrtype-desc-${r.id}`} value={r.description} onChange={(e) => patch(r.id, { description: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
                  </div>
                  <div className="md:col-span-2 flex flex-wrap items-center gap-4">
                    <button data-testid={`qrtype-enabled-${r.id}`} onClick={() => patch(r.id, { enabled: !r.enabled })}
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${r.enabled ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"}`}>
                      <Icon name={r.enabled ? "ToggleRight" : "ToggleLeft"} className="h-4 w-4" />{r.enabled ? "Enabled" : "Disabled"}
                    </button>
                    <button data-testid={`qrtype-featured-${r.id}`} onClick={() => patch(r.id, { featured: !r.featured })}
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${r.featured ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                      <Icon name="Star" className={`h-4 w-4 ${r.featured ? "fill-primary" : ""}`} />Featured
                    </button>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Plan access:</span>
                      {plans.length === 0 && <span className="text-xs text-muted-foreground">All plans</span>}
                      {plans.map((p) => {
                        const on = r.plans.length === 0 || r.plans.includes(p.key);
                        return (
                          <button key={p.key} data-testid={`qrtype-plan-${r.id}-${p.key}`} onClick={() => togglePlan(r.id, p.key)}
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold border ${r.plans.includes(p.key) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
                            title={r.plans.length === 0 ? "All plans (no restriction)" : ""}>
                            {p.label}
                          </button>
                        );
                      })}
                      {r.plans.length === 0 && <span className="text-[11px] text-muted-foreground">(no restriction — every plan)</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
